import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  IAmenity,
  IGallery,
  IHomeInitialState,
  IRoom,
  ITestimonial,
} from "./homeModel";
import {
  getAllActiveAmenities,
  getAllActiveBanner,
  getAllActiveGallerys,
  getAllActiveRooms,
  getAllActiveSettings,
  getAllActiveTestimonials,
  getAllCMSByURL,
  getAllMetaData,
  getHomeDetails,
  insertInquiry,
} from "./home.services";

const initialState: IHomeInitialState = {
  loading: false,
  galleryloading: false,
  roomLoading: false,
  formLoading: false,
  testimonialLoading: false,
  amenitiesLoading: false,
  error: "",
  homeDetails: {},
  CMSList: [],
  BannerList: [],
  SettingList: [],
  MetaDetails: {
    MetaTitle: "",
    MetaKeywords: "",
    MetaDescription: "",
  },
  GalleryList: [],
  RoomList: [],
  TestimonialList: [],
  AmenitiesList: [],
};

const homeSlice = createSlice({
  name: "home",
  initialState,
  reducers: {
    setHomeDetails: (state, action: PayloadAction<any>) => {
      state.homeDetails = action.payload;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getHomeDetails.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getHomeDetails.fulfilled, (state, action) => {
      state.loading = false;
      state.homeDetails = action.payload;
      state.error = "";
    });
    builder.addCase(getHomeDetails.rejected, (state, action) => {
      state.loading = false;
      state.homeDetails = initialState.homeDetails;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getAllCMSByURL.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllCMSByURL.fulfilled, (state, action) => {
      state.loading = false;
      state.CMSList = action.payload || [];
      state.error = "";
    });
    builder.addCase(getAllCMSByURL.rejected, (state, action) => {
      state.loading = false;
      state.CMSList = [];
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getAllMetaData.pending, (state) => {
      state.loading = true;
      state.MetaDetails = initialState.MetaDetails;
    });
    builder.addCase(getAllMetaData.fulfilled, (state, action) => {
      state.loading = false;
      state.MetaDetails = action.payload;
      state.error = "";
    });
    builder.addCase(getAllMetaData.rejected, (state, action) => {
      state.loading = false;
      state.MetaDetails = initialState.MetaDetails;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getAllActiveSettings.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllActiveSettings.fulfilled, (state, action) => {
      state.loading = false;
      state.SettingList = action.payload;
      state.error = "";
    });
    builder.addCase(getAllActiveSettings.rejected, (state, action) => {
      state.loading = false;
      state.SettingList = initialState.SettingList;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getAllActiveBanner.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllActiveBanner.fulfilled, (state, action) => {
      state.loading = false;
      state.BannerList = action.payload || [];
      state.error = "";
    });
    builder.addCase(getAllActiveBanner.rejected, (state, action) => {
      state.loading = false;
      state.BannerList = [];
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(insertInquiry.pending, (state) => {
      state.formLoading = true;
      state.error = "";
    });
    builder.addCase(insertInquiry.fulfilled, (state) => {
      state.formLoading = false;
    });
    builder.addCase(insertInquiry.rejected, (state, action) => {
      state.formLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(getAllActiveGallerys.pending, (state) => {
      state.galleryloading = true;
      state.error = "";
    });
    builder.addCase(
      getAllActiveGallerys.fulfilled,
      (state, action: PayloadAction<IGallery[]>) => {
        state.galleryloading = false;
        state.GalleryList = action.payload || [];
      },
    );
    builder.addCase(getAllActiveGallerys.rejected, (state, action) => {
      state.galleryloading = false;
      state.error = action.error.message || "Something went wrong";
      state.GalleryList = [];
    });

    builder.addCase(getAllActiveRooms.pending, (state) => {
      state.roomLoading = true;
      state.error = "";
    });
    builder.addCase(
      getAllActiveRooms.fulfilled,
      (state, action: PayloadAction<IRoom[]>) => {
        state.roomLoading = false;
        state.RoomList = action.payload || [];
      },
    );
    builder.addCase(getAllActiveRooms.rejected, (state, action) => {
      state.roomLoading = false;
      state.error = action.error.message || "Something went wrong";
      state.RoomList = [];
    });

    builder.addCase(getAllActiveTestimonials.pending, (state) => {
      state.testimonialLoading = true;
      state.error = "";
    });
    builder.addCase(
      getAllActiveTestimonials.fulfilled,
      (state, action: PayloadAction<ITestimonial[]>) => {
        state.testimonialLoading = false;
        state.TestimonialList = action.payload || [];
      },
    );
    builder.addCase(getAllActiveTestimonials.rejected, (state, action) => {
      state.testimonialLoading = false;
      state.error = action.error.message || "Something went wrong";
      state.TestimonialList = [];
    });

    builder.addCase(getAllActiveAmenities.pending, (state) => {
      state.amenitiesLoading = true;
      state.error = "";
    });
    builder.addCase(
      getAllActiveAmenities.fulfilled,
      (state, action: PayloadAction<IAmenity[]>) => {
        state.amenitiesLoading = false;
        state.AmenitiesList = action.payload || [];
      },
    );
    builder.addCase(getAllActiveAmenities.rejected, (state, action) => {
      state.amenitiesLoading = false;
      state.error = action.error.message || "Something went wrong";
      state.AmenitiesList = [];
    });
  },
});

export const { setHomeDetails } = homeSlice.actions;

export default homeSlice.reducer;
